// Shared optical surfaces for navigation and search. No page snapshots or image re-fetches.
const SVG_NS = 'http://www.w3.org/2000/svg';

export function supportsRefraction(navigatorInfo, supportsURLFilter) {
  // WebKit accepts the syntax without reliably rendering SVG backdrop filters.
  // Keep the CSS material on other engines until their rendering path is verified.
  const brands = navigatorInfo.userAgentData?.brands || [];
  const chromium = brands.some(({ brand }) => /Chromium|Google Chrome|Microsoft Edge/.test(brand))
    || /(?:Chrome|Chromium|Edg|OPR)\//.test(navigatorInfo.userAgent || '');
  return supportsURLFilter && chromium && !/(?:CriOS|FxiOS|EdgiOS)\//.test(navigatorInfo.userAgent || '');
}

export function createLensMap(width, height, radius = 28, { softTop = false } = {}) {
  if (!(width > 0 && height > 0 && Number.isFinite(width + height))) {
    throw new RangeError('The glass surface must have finite, positive dimensions.');
  }
  // Half-resolution geometry is ample for a smooth lens; cap memory on large screens.
  const mapWidth = Math.max(1, Math.min(512, Math.ceil(width * .5)));
  const mapHeight = Math.max(1, Math.min(512, Math.ceil(height * .5)));
  const pixels = new Uint8ClampedArray(mapWidth * mapHeight * 4);
  const corner = Math.max(0, Math.min(radius, width / 2, height / 2));
  const rim = Math.min(28, width / 4, height / 3);
  for (let y = 0; y < mapHeight; y++) {
    for (let x = 0; x < mapWidth; x++) {
      const px = (x + .5) * width / mapWidth - width / 2;
      const py = (y + .5) * height / mapHeight - height / 2;
      const qx = Math.abs(px) - (width / 2 - corner);
      const qy = Math.abs(py) - (height / 2 - corner);
      const ex = Math.max(qx, 0), ey = Math.max(qy, 0);
      const outside = Math.hypot(ex, ey);
      const depth = -(outside + Math.min(Math.max(qx, qy), 0) - corner);
      let dx = 0, dy = 0, frost = 1;
      if (depth < rim) {
        const t = Math.max(0, depth / rim);
        frost = t * t * (3 - 2 * t);
        // One monotonic shoulder, with zero slope at either end. The old sine
        // bump folded source coordinates back over themselves near the edge.
        const nx = outside > 0 ? ex / outside : qx > qy ? 1 : 0;
        const ny = outside > 0 ? ey / outside : qx > qy ? 0 : 1;
        // A viewport-attached top cannot sample scenery above the screen like
        // the free bottom edge. Diffuse that shoulder and reduce its bend;
        // retain the same smooth curve into the centre and the other edges.
        const topDiffusion = softTop && py < 0 ? .65 * ny : 0;
        const bend = (1 - frost) * (1 - topDiffusion);
        frost = 1 - bend;
        dx = Math.sign(px) * nx * bend;
        dy = Math.sign(py) * ny * bend;
      }
      const i = (y * mapWidth + x) * 4;
      pixels[i] = Math.round(127.5 + 127.5 * dx);
      pixels[i + 1] = Math.round(127.5 + 127.5 * dy);
      // Blue also encodes the transition from the clear rim to the frosted centre.
      pixels[i + 2] = Math.round(255 * frost);
      pixels[i + 3] = 255;
    }
  }
  return { width: mapWidth, height: mapHeight, pixels };
}

function svgNode(tag, attributes = {}) {
  const element = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
  return element;
}

function createBackdropLens(root, id, { blur = 20, scale = 12 } = {}) {
  const definitions = svgNode('svg', { 'aria-hidden': 'true', focusable: 'false', class: 'liquid-filter-defs' });
  const defs = svgNode('defs');
  const filter = svgNode('filter', { id, filterUnits: 'userSpaceOnUse', 'color-interpolation-filters': 'sRGB' });
  const field = svgNode('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'lens-field' });
  // Diffuse before displacement so both paths sample the same optical position.
  // A softly diffused edge avoids a razor-sharp strip beside the frosted centre.
  const edge = svgNode('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: 4, edgeMode: 'duplicate', result: 'edge-source' });
  const centre = svgNode('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: blur, edgeMode: 'duplicate', result: 'centre-source' });
  const refract = (input, result) => svgNode('feDisplacementMap', { in: input, in2: 'lens-field', scale, xChannelSelector: 'R', yChannelSelector: 'G', result });
  const interior = svgNode('feColorMatrix', { in: 'lens-field', type: 'matrix', values: '0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 1 0 0', result: 'interior' });
  const composite = svgNode('feComposite', { in: 'centre-glass', in2: 'interior', operator: 'in', result: 'soft-interior' });
  const merge = svgNode('feMerge');
  merge.append(svgNode('feMergeNode', { in: 'edge-glass' }), svgNode('feMergeNode', { in: 'soft-interior' }));
  filter.append(field, edge, centre, refract('edge-source', 'edge-glass'), refract('centre-source', 'centre-glass'), interior, composite, merge, svgNode('feColorMatrix', { type: 'saturate', values: 1.3 }));
  defs.append(filter);
  definitions.append(defs);
  root.append(definitions);
  return {
    value: `url("#${id}")`,
    dispose() { definitions.remove(); },
    update(width, height, url) {
      const padding = Math.ceil(blur * 3 + scale / 2 + 2);
      for (const [key, value] of Object.entries({ x: -padding, y: -padding, width: width + padding * 2, height: height + padding * 2 })) filter.setAttribute(key, value);
      field.setAttribute('width', width);
      field.setAttribute('height', height);
      field.setAttribute('href', url);
    },
  };
}

async function lensMapURL(width, height, radius, cache, { limit = 3, softTop = false } = {}) {
  const key = `${width}:${height}:${radius}:${softTop}`;
  if (cache.has(key)) return cache.get(key);
  const map = createLensMap(width, height, radius, { softTop });
  const canvas = document.createElement('canvas');
  canvas.width = map.width; canvas.height = map.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is unavailable');
  const data = context.createImageData(map.width, map.height);
  data.data.set(map.pixels);
  context.putImageData(data, 0, 0);
  const url = canvas.toDataURL('image/png');
  const image = new Image();
  image.src = url;
  await image.decode();
  cache.set(key, url);
  if (cache.size > limit) cache.delete(cache.keys().next().value);
  return url;
}

// Adapted from Quiet-Glass. React owns the lifecycle; no work runs on scroll.
export function enhanceNavigationGlass(header) {
  const preferences = ['(prefers-reduced-transparency: reduce)', '(prefers-contrast: more)', '(forced-colors: active)', '(pointer: fine)'].map(query => matchMedia(query));
  const canRefract = supportsRefraction(navigator, CSS.supports('backdrop-filter', 'url("#gallery-nav-lens")'));
  const maps = new Map();
  let lens, timer, revision = 0, lastSize = '', disposed = false;
  const simplified = () => preferences.slice(0, 3).some(query => query.matches);
  const enabled = () => canRefract && preferences[3].matches && !simplified() && !document.hidden;
  function reset() {
    header.style.removeProperty('--nav-glass-filter');
    header.dataset.glassOptics = simplified() ? 'reduced' : 'css';
    lastSize = '';
  }
  async function update(ticket) {
    if (disposed || !enabled()) return;
    const width = header.offsetWidth, height = header.offsetHeight;
    const key = `${width}:${height}`;
    if (!width || !height || key === lastSize) return;
    try {
      const url = await lensMapURL(width, height, 0, maps, { softTop: true });
      if (disposed || ticket !== revision || !enabled()) return;
      lens ||= createBackdropLens(document.body, 'gallery-nav-lens', { blur: 16, scale: 8 });
      lens.update(width, height, url);
      header.style.setProperty('--nav-glass-filter', lens.value);
      header.dataset.glassOptics = 'refraction';
      lastSize = key;
    } catch { if (!disposed && ticket === revision) reset(); }
  }
  function schedule() {
    clearTimeout(timer);
    const ticket = ++revision;
    timer = setTimeout(() => update(ticket), 100);
  }
  const observer = new ResizeObserver(schedule);
  function sync() {
    observer.disconnect();
    clearTimeout(timer);
    ++revision;
    if (!enabled()) { reset(); return; }
    observer.observe(header);
    schedule();
  }
  preferences.forEach(query => query.addEventListener('change', sync));
  document.addEventListener('visibilitychange', sync);
  sync();
  return () => {
    disposed = true;
    ++revision;
    clearTimeout(timer);
    observer.disconnect();
    preferences.forEach(query => query.removeEventListener('change', sync));
    document.removeEventListener('visibilitychange', sync);
    lens?.dispose();
    maps.clear();
    reset();
  };
}
